import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import { ChangeEvent, MouseEventHandler, useState } from "react";
import { useDispatch } from "react-redux";

import { ApiService, InsurancePlan, Lab, PatientInsurance } from "pwapi";

import {
  updateInsurance,
  createInsurance,
} from "shared/actions/admin/adminPatientActions";
import AdvancedInsurancePlanLookup from "shared/components/AdvancedInsurancePlanLookup";
import { InsuranceListItem } from "shared/components/AsyncListItems";
import {
  PW_YOUNGEST_ACCEPTABLE_BIRTHDATE,
  WORLDS_OLDEST_PERSONS_BIRTHDATE,
} from "shared/utils/dateUtils";
import { locationOptions } from "shared/utils/usRegions";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import {
  initialValuesForPatientInsurance,
  shallowPatientInsuranceComparison,
} from "./insuranceFormUtils";

type InsuranceFormProps = {
  insurance: PatientInsurance;
  primaryInsurance?: PatientInsurance;
  caseId: number;
  patientId: number;
  onDelete?: Function;
  onClose: MouseEventHandler<HTMLButtonElement>;
  disableSubmit?: boolean;
  lab?: Lab;
};

const InsuranceForm = ({
  insurance,
  primaryInsurance,
  caseId,
  patientId,
  onDelete,
  onClose,
  disableSubmit = false,
  lab,
}: InsuranceFormProps) => {
  const dispatch = useDispatch();
  const [saveDisabled, setSaveDisabled] = useState(false);

  const initialValues = initialValuesForPatientInsurance(
    insurance,
    !!primaryInsurance?.id
  );

  const handleOnSubmit = (values: PatientInsurance) => {
    if (disableSubmit) return;

    setSaveDisabled(true);

    const submissionForm = {
      ...shallowPatientInsuranceComparison(
        insurance,
        values,
        !!(insurance.is_manual || lab?.use_manual_insurance)
      ),
      ...(insurance?.id
        ? { id: insurance.id }
        : { primary_provider: !!values.primary_provider }),
      patient_id: patientId,
    };

    const payload = {
      insurance: submissionForm,
      caseId,
      primaryInsurance,
      patientId,
      onClose,
      onSaveError: () => {
        setSaveDisabled(false);
      },
    };

    insurance.id
      ? dispatch(updateInsurance(payload))
      : dispatch(createInsurance(payload));
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => handleOnSubmit(values)}
    >
      {({ resetForm, dirty, values, setValues }) => (
        <Form className="AdminPanelForm">
          <div className="panelContainer">
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Insured Details
              </legend>
              <Field name="relationship_to_patient">
                {({ field }: any) => (
                  <SelectInput
                    label="Relationship to Patient"
                    options={["SELF", "SPOUSE", "PARENT", "OTHER"].map((a) => ({
                      label: a,
                      value: a,
                    }))}
                    {...field}
                    disabled={disableSubmit}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setValues({
                        ...((values.relationship_to_patient === "SELF" &&
                          e.target.value !== "SELF") ||
                        (values.relationship_to_patient !== "SELF" &&
                          e.target.value === "SELF")
                          ? {
                              ...values,
                              insured_last_name: "",
                              insured_first_name: "",
                              insured_date_of_birth: "",
                            }
                          : values),
                        relationship_to_patient: e.target.value,
                      });
                    }}
                  />
                )}
              </Field>
              <div className="panelContainer__inputGroup">
                <Field name="insured_last_name">
                  {({ field }: any) => (
                    <TextInput
                      label="Last Name"
                      id="insured_last_name"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <Field name="insured_first_name">
                  {({ field }: any) => (
                    <TextInput
                      label="First Name"
                      id="insured_first_name"
                      required={values.relationship_to_patient !== "SELF"}
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <Field name="insured_date_of_birth">
                  {({ field, form }: any) => (
                    <CustomInput
                      label="Date of birth"
                      id="insured_date_of_birth"
                    >
                      <DateInput
                        {...field}
                        value={field.value || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          form.setFieldValue(
                            "insured_date_of_birth",
                            e.target.value
                          )
                        }
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        maxDate={PW_YOUNGEST_ACCEPTABLE_BIRTHDATE}
                        minDate={WORLDS_OLDEST_PERSONS_BIRTHDATE}
                      />
                    </CustomInput>
                  )}
                </Field>
              </div>
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Insurance Details
              </legend>
              <div className="panelContainer__inputGroup">
                <Field name="primary_provider">
                  {({ field, form }: any) => {
                    return (
                      <div className="panelContainer__inputGroup">
                        <input
                          type="checkbox"
                          {...field}
                          id="primaryProvider"
                          checked={field.value || false}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            form.setFieldValue(
                              "primary_provider",
                              e.target.checked
                            );
                          }}
                        />
                        <label htmlFor="primaryProvider">
                          Primary Insurance
                        </label>
                      </div>
                    );
                  }}
                </Field>
                {values.primary_provider && (
                  <Field name="has_abn">
                    {({ field, form }: any) => {
                      return (
                        <div className="panelContainer__inputGroup">
                          <input
                            type="checkbox"
                            {...field}
                            id="has_abn"
                            checked={field.value || false}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              form.setFieldValue("has_abn", e.target.checked);
                            }}
                          />
                          <label htmlFor="has_abn">Has ABN</label>
                        </div>
                      );
                    }}
                  </Field>
                )}
                <div />
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="group_employer_id">
                  {({ field }: any) => (
                    <TextInput
                      label="Group Employer Id"
                      id="group_employer_id"
                      {...field}
                      disabled={disableSubmit}
                      maxLength="50"
                    />
                  )}
                </Field>
                <Field name="policy_number">
                  {({ field }: any) => (
                    <TextInput
                      label="Policy Number"
                      id="policy_number"
                      {...field}
                      disabled={disableSubmit}
                      maxLength="20"
                    />
                  )}
                </Field>
                <Field name="preapproval_id">
                  {({ field }: any) => (
                    <TextInput
                      label="Preauthorization ID"
                      id="preapproval_id"
                      {...field}
                      disabled={disableSubmit}
                      maxLength="128"
                    />
                  )}
                </Field>
              </div>
              {insurance.is_manual || lab?.use_manual_insurance ? (
                <>
                  <Field name="insurance_company_name">
                    {({ field }: any) => (
                      <TextInput
                        label="Company Name"
                        id="insurance_company_name"
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="insurance_company_address">
                    {({ field }: any) => (
                      <TextInput
                        label="Company Street Line 1"
                        id="insurance_company_address"
                        {...field}
                        disabled={disableSubmit || !lab}
                      />
                    )}
                  </Field>
                  <Field name="insurance_company_address2">
                    {({ field }: any) => (
                      <TextInput
                        label="Company Street Line 2"
                        id="insurance_company_address2"
                        {...field}
                        disabled={disableSubmit || !lab}
                      />
                    )}
                  </Field>
                  <div className="panelContainer__inputGroup">
                    <Field name="insurance_company_city">
                      {({ field }: any) => (
                        <TextInput
                          label="Company City"
                          id="insurance_company_city"
                          {...field}
                          disabled={disableSubmit || !lab}
                        />
                      )}
                    </Field>
                    <Field name="insurance_company_state">
                      {({ field }: any) => (
                        <SelectInput
                          label="Company State"
                          options={locationOptions}
                          {...field}
                          disabled={disableSubmit || !lab}
                        />
                      )}
                    </Field>
                    <Field name="insurance_company_zipcode">
                      {({ field }: any) => (
                        <TextInput
                          label="Company Zip Code"
                          id="insurance_company_zipcode"
                          {...field}
                          disabled={disableSubmit || !lab}
                        />
                      )}
                    </Field>
                  </div>
                </>
              ) : !lab ? (
                <>
                  Cannot update insurance company info, the case's Accessioning
                  Lab is missing.
                </>
              ) : (
                <Field name="plan">
                  {({ field, form }: any) => (
                    <CustomInput label="Insurance Plan">
                      <div className="lookUpTable">
                        {field.value ? (
                          <InsuranceListItem object={field.value} />
                        ) : (
                          <div
                            style={{
                              padding: "0.6em",
                              color: "var(--imperialRed)",
                            }}
                          >
                            <FontAwesomeIcon
                              icon="exclamation-circle"
                              style={{ marginRight: "0.3em" }}
                            />
                            Missing Insurance Plan
                          </div>
                        )}
                        <AdvancedInsurancePlanLookup
                          TextInput={TextInput}
                          SelectInput={SelectInput}
                          selectedPlan={field.value}
                          spreadInputs
                          apiOperation={ApiService.listAdminInsurancePlans}
                          onSelectItem={(insurance: InsurancePlan) => {
                            form.setValues({
                              ...form.values,
                              plan:
                                insurance && insurance.id !== field.value?.id
                                  ? insurance
                                  : null,
                            });
                          }}
                        />
                      </div>
                    </CustomInput>
                  )}
                </Field>
              )}
            </fieldset>
          </div>
          <footer>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton onClick={onClose}>
                Cancel
              </PathologyWatchButton>
              {!!onDelete && (
                <PathologyWatchButton
                  onClick={() => {
                    onDelete();
                  }}
                  color="red"
                  disabled={disableSubmit}
                >
                  Delete
                </PathologyWatchButton>
              )}
            </div>

            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  resetForm();
                }}
                disabled={!dirty}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="green"
                disabled={!dirty || disableSubmit || saveDisabled}
              >
                Save
              </PathologyWatchButton>
            </div>
          </footer>
        </Form>
      )}
    </Formik>
  );
};

export default InsuranceForm;
